import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { apiFetch, setRefreshFn, setOnAuthFailure } from '@/lib/apiFetch';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not auth'd, object = signed in
  const [loading, setLoading] = useState(true);
  const userRef = useRef(null);
  userRef.current = user;

  const checkAuth = useCallback(async () => {
    try {
      // skipAuthRetry: /auth/me is itself the auth check — retrying via /refresh
      // is handled below via the refresh-fn override.
      const { data } = await apiFetch('/api/auth/me', { skipAuthRetry: true });
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  // Wire apiFetch's refresh + auth-failure callbacks to live AuthContext state.
  // The retry-on-401 flow inside apiFetch will call our refreshFn (which itself
  // uses skipAuthRetry to avoid recursion), and on irrecoverable failure will
  // call onAuthFailure so the SPA can flip user state to "not signed in".
  useEffect(() => {
    setRefreshFn(async () => {
      try {
        const r = await fetch('/api/auth/refresh', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Accept': 'application/json' },
        });
        return r.ok;
      } catch {
        return false;
      }
    });
    setOnAuthFailure(() => {
      // Only clear if we previously had a signed-in user (avoid bouncing the
      // landing/auth pages where user is already false).
      if (userRef.current) setUser(false);
    });
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await apiFetch('/api/auth/login', {
      method: 'POST',
      body: { email, password },
      skipAuthRetry: true,
    });
    setUser(data);
    return data;
  };

  const register = async (email, password, name) => {
    const { data } = await apiFetch('/api/auth/register', {
      method: 'POST',
      body: { email, password, name },
      skipAuthRetry: true,
    });
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await apiFetch('/api/auth/logout', { method: 'POST', skipAuthRetry: true });
    } finally {
      setUser(false);
    }
  };

  const refreshToken = async () => {
    try {
      await apiFetch('/api/auth/refresh', { method: 'POST', skipAuthRetry: true });
      await checkAuth();
    } catch {
      setUser(false);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshToken }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
